import { useEffect, useMemo, useRef, useState } from 'react'
import PillBadge from './PillBadge'
import Reveal from '../lib/Reveal'
import {
  Router,
  Phone,
  Clapperboard,
  Camera,
  Cpu,
  KeyRound,
  ArrowLeft,
  ArrowRight,
} from 'lucide-react'

// ===================== Tipos & Dados =====================

type Service = {
  key: 'internet' | 'telefonia' | 'streaming' | 'cameras' | 'automacao' | 'acesso'
  title: string
  desc: string
}

const services: Service[] = [
  { key: 'internet',  title: 'Internet Ultra Fibra',         desc: 'Conexão simétrica de alta performance até cada unidade.' },
  { key: 'telefonia', title: 'Telefonia & Números Virtuais', desc: 'PABX virtual, integração com WhatsApp e comunicação sem ruídos.' },
  { key: 'streaming', title: 'Streaming Premium',            desc: 'Planos com HBO Max, Paramount+, GloboPlay e mais.' },
  { key: 'cameras',   title: 'Vídeo Vigilância CaaS',        desc: 'Gravação em nuvem e monitoramento 24h.' },
  { key: 'automacao', title: 'Automação & IoT',              desc: 'Portaria digital, iluminação e controle remoto.' },
  { key: 'acesso',    title: 'Controle de Acesso',           desc: 'Gestão de visitantes e integrações com câmeras inteligentes.' },
]

// Ícones por serviço
const ICONS: Record<Service['key'], any> = {
  internet: Router,
  telefonia: Phone,
  streaming: Clapperboard,
  cameras: Camera,
  automacao: Cpu,
  acesso: KeyRound,
}

// Destaques (pills)
const HIGHLIGHTS: Partial<Record<Service['key'], string[]>> = {
  internet: ['Link dedicado', 'QoS e VLAN', 'IPv6 pronto'],
  telefonia: ['URA inteligente', 'SIP Trunk', 'WhatsApp Oficial'],
  streaming: ['Canais HD', 'Perfis infantis', 'Controle parental'],
  cameras: ['Cloud-first', 'IA de detecção', 'Retenção flexível'],
  automacao: ['Cenas e rotinas', 'Zigbee/Thread', 'App unificado'],
  acesso: ['QR/PIX', 'Biometria', 'Anti-passback'],
}

// ===================== UI Auxiliares =====================

function Media({ s }: { s: Service }) {
  return (
    <div className="relative w-full overflow-hidden rounded-xl">
      <img
        src={`/images/thumb-${s.key}.png`}
        alt={s.title}
        loading="lazy"
        className="block h-36 w-full select-none object-cover sm:h-40 lg:h-44"
        draggable={false}
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
    </div>
  )
}

function CTA({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-sm text-white shadow-sm backdrop-blur-sm transition hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/30"
    >
      {children}
    </a>
  )
}

function Highlights({ items }: { items?: string[] }) {
  if (!items?.length) return null
  return (
    <ul className="mt-3 flex flex-wrap gap-2 text-xs text-white/90">
      {items.map((it, i) => (
        <li
          key={i}
          className="rounded-full border border-white/15 bg-white/5 px-3 py-1 leading-5 shadow-sm"
        >
          {it}
        </li>
      ))}
    </ul>
  )
}

function ServiceCard({ s }: { s: Service }) {
  const Icon = ICONS[s.key]
  return (
    <article
      className={[
        'group relative flex h-full flex-col overflow-hidden rounded-2xl border border-white/15 bg-white/5',
        'ring-1 ring-inset ring-white/10 shadow-sm backdrop-blur-sm transition-shadow hover:shadow-md focus-within:shadow-md',
      ].join(' ')}
      aria-label={s.title}
    >
      {/* Pill no topo pra dar respiro */}
      <div className="absolute right-3 top-3 z-10">
        <PillBadge>premium</PillBadge>
      </div>

      <Media s={s} />

      <div className="flex flex-1 flex-col p-5">
        <div className="mb-2 flex items-center gap-3">
          {/* bolinha do ícone simétrica (fixa) */}
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/5 ring-1 ring-inset ring-white/10">
            <Icon className="h-5 w-5 text-white/90" />
          </span>
          <h4 className="font-semibold leading-tight text-white/[0.98]">{s.title}</h4>
        </div>

        <p className="mt-1 line-clamp-2 text-sm text-white/70">{s.desc}</p>
        <Highlights items={HIGHLIGHTS[s.key]?.slice(0, 2)} />

        <div className="mt-auto flex items-center justify-end pt-4">
          <CTA href="#cta">Saiba mais</CTA>
        </div>
      </div>
    </article>
  )
}

// ===================== Helpers de autoplay =====================

function useAutoplay(enabled: boolean, delayMs: number, cb: () => void) {
  const timerRef = useRef<number | null>(null)
  const clear = () => {
    if (timerRef.current) window.clearInterval(timerRef.current)
    timerRef.current = null
  }
  useEffect(() => {
    clear()
    if (!enabled) return
    timerRef.current = window.setInterval(cb, delayMs)
    return clear
  }, [enabled, delayMs, cb])
  return { clear }
}

// ===================== Carrossel Paginado (4 cards, sem scroll) =====================

function Carousel({ items }: { items: Service[] }) {
  const PER_PAGE = 4 // **fixo**: 4 cards por tela
  const [page, setPage] = useState(0)
  const totalPages = Math.max(1, Math.ceil(items.length / PER_PAGE))

  // autoplay discreto
  const [paused, setPaused] = useState(false)
  const next = () => setPage((p) => (p + 1) % totalPages)
  const prev = () => setPage((p) => (p - 1 + totalPages) % totalPages)
  useAutoplay(!paused, 4500, next)

  // fatia atual e preenchimento:
  // quando a última página tiver < 4 itens, repetir os **dois primeiros** (não os últimos)
  const start = page * PER_PAGE
  const slice = useMemo(() => items.slice(start, start + PER_PAGE), [items, start])
  const deficit = Math.max(0, PER_PAGE - slice.length)
  const headRepeat = deficit > 0 ? items.slice(0, deficit) : []   // <- AQUI muda
  const pageItems = [...slice, ...headRepeat]

  return (
    <div
      className="relative px-12"  // menos padding lateral para “encurtar” a seção
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="Carrossel de serviços (4 por vez)"
    >
      {/* Setas EXTERNAS aos cards */}
      <button
        type="button"
        aria-label="Anterior"
        onClick={prev}
        className="absolute left-0 top-1/2 z-20 -translate-y-1/2 -translate-x-full rounded-full border border-white/10 bg-white/5 p-3 text-white shadow-sm backdrop-blur-sm hover:bg-white/10"
      >
        <ArrowLeft className="h-5 w-5" />
      </button>
      <button
        type="button"
        aria-label="Próxima"
        onClick={next}
        className="absolute right-0 top-1/2 z-20 -translate-y-1/2 translate-x-full rounded-full border border-white/10 bg-white/5 p-3 text-white shadow-sm backdrop-blur-sm hover:bg-white/10"
      >
        <ArrowRight className="h-5 w-5" />
      </button>

      {/* Grid fixo 4 itens — SEM SCROLL */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {pageItems.map((s, i) => (
          <Reveal key={`${s.key}-${i}`} delay={i * 0.04}>
            <ServiceCard s={s} />
          </Reveal>
        ))}
      </div>

      {/* Indicadores discretos */}
      <div className="mt-3 flex items-center justify-center gap-2">
        {Array.from({ length: totalPages }).map((_, i) => (
          <span
            key={i}
            aria-label={`Ir para página ${i + 1}`}
            onClick={() => setPage(i)}
            className={[
              'h-1.5 w-6 cursor-pointer rounded-full',
              i === page ? 'bg-white/80' : 'bg-white/20 hover:bg-white/30',
            ].join(' ')}
          />
        ))}
      </div>
    </div>
  )
}

// ===================== Seção =====================

export default function Services() {
  const ordered = useMemo(() => services, [])

  return (
    <section
      id="servicos"
      className="relative isolate bg-background py-12 md:py-16" // menos padding vertical
      aria-labelledby="servicos-heading"
    >
      {/* Fundo sutil (menor) */}
      <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden>
        <div className="absolute inset-x-0 top-[-16%] mx-auto h-[320px] w-[90%] rounded-[100%] bg-gradient-to-b from-fuchsia-500/10 to-transparent blur-3xl" />
      </div>

      <div className="mx-auto max-w-7xl px-4">
        <header className="max-w-3xl">
          <h2 id="servicos-heading" className="text-2xl md:text-4xl font-extrabold text-white">
            Uma Infra, Tudo Conectado
          </h2>
          <p className="mt-2 max-w-3xl text-white/70">
            Centralizamos Internet, Telefonia, Streaming, Segurança e Automação para simplificar sua operação.
          </p>
        </header>

        <div className="mt-6">
          <Carousel items={ordered} />
        </div>
      </div>
    </section>
  )
}
